import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Fragment, useState, type ReactNode } from 'react'
import type { CreateDeal, ManualPayment, RefundDecision, ZahlungPatch } from '../../../../shared/schemas/corrections'
import type {
  AbschlussZeile, AuditZeile, ProduktMitBegriffen, PruefenEintrag, TeamZeile, ZahlungZeile,
} from '../../../api'
import { api } from '../../../api'
import { Betrag } from '../../../components/Betrag'
import { Fahne } from '../../../components/Fahne'
import { Feld } from '../../../components/Feld'
import { Knopf } from '../../../components/Knopf'
import { useInsBild } from '../../../components/insBild'
import {
  AbschlussAnlegenDialog, AbschlussZuweisenDialog, DatumSetzenDialog, NettoBruttoDialog, ProduktZuweisenDialog,
  RueckzahlungDialog, ZahlungIgnorierenDialog, ZahlungVonHandDialog,
  type BeteiligterOption, type ProduktOption,
} from '../../../components/korrekturen'
import { Leiste } from '../../../components/Leiste'
import { Tabelle } from '../../../components/Tabelle'
import { Verweis } from '../../../components/Verweis'
import { TabellenZustand, Zustand, fehlermeldung } from '../../../components/Zustand'
import { datum, datumZeit } from '../../../format'
import { pruefKind } from '../../../pruefkinds'
import type { SeitenProps } from '../../../screen'

export interface ZahlungenSuche {
  status?: string
  von?: string
  bis?: string
  /** Die aufgeklappte Zahlung — auch sie steht in der URL, damit ein Link auf sie zeigt. */
  zeile?: string
}

/**
 * Die neun Abgleichstatus aus engine/types.ts, auf Deutsch. Reiner Oberflächentext.
 *
 * Sechs davon sind zugleich Prüflisten-Arten (`match:<status>`) und tragen dort schon ihre deutsche
 * Überschrift. Die wird hier geholt statt abgeschrieben: zwei Tabellen mit derselben Formulierung
 * driften auseinander, und dann heißt dieselbe Zahlung in der Prüfliste anders als hier. Nur die drei
 * Status, zu denen es keine Prüfzeile gibt, stehen als Text in dieser Datei.
 */
export const ABGLEICH_TEXT: Readonly<Record<string, string | undefined>> = {
  zugeordnet: 'Zugeordnet',
  manuell_zugeordnet: 'Manuell zugeordnet',
  ignoriert: 'Ignoriert',
  kein_produkt: pruefKind('match:kein_produkt').titel,
  kein_match: pruefKind('match:kein_match').titel,
  kein_aktiver_abschluss: pruefKind('match:kein_aktiver_abschluss').titel,
  mehrere_abschluesse: pruefKind('match:mehrere_abschluesse').titel,
  kontakt_fehlt: pruefKind('match:kontakt_fehlt').titel,
  manuell_pruefen: pruefKind('match:manuell_pruefen').titel,
}

function abgleichText(status: string): string {
  return (Object.hasOwn(ABGLEICH_TEXT, status) ? ABGLEICH_TEXT[status] : undefined) ?? status
}

const ZUGEORDNET = new Set(['zugeordnet', 'manuell_zugeordnet'])

/** So viele <th> stehen im Kopf der Tabelle — die Zustandszeilen darunter spannen genau darüber. */
const SPALTEN = 8

/**
 * Genau die drei Aktionen, die der Server auf `entity_type = 'customer_invoice'` schreibt
 * (services/corrections.ts) — und damit die einzigen, die die Abfrage unten überhaupt zurückgibt.
 * Alles andere zeigt seinen technischen Namen, statt hier eine Abdeckung vorzutäuschen.
 */
const PROTOKOLL_TEXT: Readonly<Record<string, string | undefined>> = {
  'invoice.manual_deal': 'Abschluss manuell zugeordnet',
  'invoice.manual_product': 'Produkt manuell gesetzt',
  'invoice.ignore': 'Ignorierung geändert',
}

function protokollText(action: string): string {
  return (Object.hasOwn(PROTOKOLL_TEXT, action) ? PROTOKOLL_TEXT[action] : undefined) ?? action
}

/**
 * `invoice_url` ist ungeprüfter Webhook-Text (hooks/parse.ts trimmt ihn, mehr nicht) und die einzige Stelle,
 * an der fremde Zeichenketten hier zum Linkziel werden. Nur http und https werden verlinkt; alles andere
 * steht als Text da, statt ein `javascript:`-Ziel anzubieten (an dem React 19 den Bildschirm weiß färbt).
 */
function istWebLink(url: string): boolean {
  try {
    const schema = new URL(url).protocol
    return schema === 'http:' || schema === 'https:'
  } catch {
    return false
  }
}

type Ziel =
  | { art: 'abschluss' }
  | { art: 'produkt' }
  | { art: 'ignorieren' }
  | { art: 'anlegen' }
  | { art: 'datum'; deltaId: string }
  | { art: 'netto'; deltaId: string }
  | { art: 'refund'; deltaId: string; entscheidung: 'confirm' | 'ignore' }
  | { art: 'erfassen' }

export function ZahlungenSeite({ tenantId, suche, setzeSuche, gehZu }: SeitenProps<ZahlungenSuche>): ReactNode {
  const qc = useQueryClient()
  const [ziel, setZiel] = useState<Ziel | null>(null)
  // Die Erfolgsmeldung merkt sich, zu welcher Zahlung sie gehört, und verschwindet, sobald eine andere
  // aufgeklappt ist: „Rückzahlung bestätigt." über den Zahlen einer anderen Rechnung wäre eine Lüge.
  const [meldung, setMeldung] = useState<{ zeile: string | undefined; text: string }>({ zeile: undefined, text: '' })
  const meldungstext = meldung.zeile === suche.zeile ? meldung.text : ''

  const liste = useQuery({
    queryKey: ['zahlungen', tenantId, suche.status ?? '', suche.von ?? '', suche.bis ?? ''],
    queryFn: () => api.get<{ zahlungen: ZahlungZeile[] }>(`/api/t/${tenantId}/zahlungen`, {
      status: suche.status, from: suche.von, to: suche.bis,
    }),
  })
  const inbox = useQuery({
    queryKey: ['inbox', tenantId, ''],
    queryFn: () => api.get<{ items: PruefenEintrag[] }>(`/api/t/${tenantId}/inbox`, { kind: undefined }),
  })
  const abschluesse = useQuery({
    queryKey: ['abschluesse', tenantId],
    queryFn: () => api.get<{ abschluesse: AbschlussZeile[] }>(`/api/t/${tenantId}/abschluesse`),
  })
  const produkte = useQuery({
    queryKey: ['produkte', tenantId],
    queryFn: () => api.get<{ produkte: ProduktMitBegriffen[] }>(`/api/t/${tenantId}/produkte`),
  })
  const team = useQuery({
    queryKey: ['team', tenantId],
    queryFn: () => api.get<{ team: TeamZeile[] }>(`/api/t/${tenantId}/team`),
  })
  const protokoll = useQuery({
    queryKey: ['audit', tenantId, suche.zeile ?? ''],
    queryFn: () => api.get<{ eintraege: AuditZeile[] }>(`/api/t/${tenantId}/audit`, {
      entity: `customer_invoice:${suche.zeile ?? ''}`,
    }),
    enabled: suche.zeile !== undefined,
  })

  const korrektur = useMutation<string, Error, { meldung: string; ruf: () => Promise<unknown> }>({
    mutationFn: async (auftrag) => {
      await auftrag.ruf()
      return auftrag.meldung
    },
    onSuccess: (text) => {
      setMeldung({ zeile: suche.zeile, text })
      setZiel(null)
      // Nie den eigenen Zwischenstand raten: jede betroffene Liste wird neu geholt (Vertrag Teil 4).
      for (const schl of ['zahlungen', 'inbox', 'audit', 'provisionen', 'dashboard', 'abschluesse']) {
        void qc.invalidateQueries({ queryKey: [schl, tenantId] })
      }
    },
  })

  const zahlungen = liste.data?.zahlungen ?? []
  const gewaehlt = zahlungen.find((z) => z.id === suche.zeile) ?? null
  // Zuordnung über `subject_parent_id` (Part 24 Task 76b), nicht über die Rechnungsnummer: bei
  // einer Rate ist der Elternschlüssel genau die customer_invoice_id dieser Zahlung.
  const offeneRaten = (inbox.data?.items ?? []).filter((i) =>
    gewaehlt !== null && i.subject_type === 'payment_delta' && i.subject_parent_id === gewaehlt.id,
  )
  const produktOptionen: ProduktOption[] = (produkte.data?.produkte ?? [])
    .filter((p) => p.active === 1).map((p) => ({ id: p.id, code: p.code, name: p.name }))
  const beteiligte: BeteiligterOption[] = (team.data?.team ?? [])
    .filter((t) => t.active === 1).map((t) => ({ id: t.tenant_contractor_id, bezeichnung: `${t.display_name} (${t.short_code})` }))
  const abschlussOptionen = (abschluesse.data?.abschluesse ?? []).map((a) => ({
    id: a.id, bezeichnung: `${a.customer_name} · ${a.product_code ?? 'ohne Produkt'} · ${a.status}`,
  }))

  const pfad = gewaehlt === null ? '' : `/api/t/${tenantId}/zahlungen/${gewaehlt.id}`
  const rahmen = {
    offen: true,
    laeuft: korrektur.isPending,
    fehler: korrektur.error?.message ?? null,
    onSchliessen: () => { korrektur.reset(); setZiel(null) },
  }
  const erklaerung = gewaehlt === null ? null : (
    <p><strong>{gewaehlt.invoice_number}</strong> — {gewaehlt.customer_name} · {gewaehlt.invoice_name}</p>
  )

  // Das Detail steht als eigene Tabellenzeile direkt unter der gewählten Zahlung, nicht unter der
  // ganzen Tabelle: wer eine Zahlung aufklappt, soll nicht nach unten suchen müssen (Bastian,
  // 11.10.2026).
  useInsBild(gewaehlt === null ? null : `zahlung-detail-${gewaehlt.id}`)
  const detail = gewaehlt === null ? null : (
        <div className="detail zeile-detail-inhalt" id={`zahlung-detail-${gewaehlt.id}`}>
          {/*
            Vier benannte Bereiche nebeneinander statt einer verschachtelten Detailkarte, und die Trennung
            verläuft entlang der Frage, die dieser Bildschirm beantwortet: was in unseren Büchern steht
            gegen das, was FinSuites über die Rechnung meldet. Beide Blöcke tragen dieselben Zahlen, wenn
            alles stimmt — in EINEM Bereich stünde dieselbe Summe dann zweimal untereinander, und eine
            Vorlesesoftware läse zwei gleich benannte Beträge vor, ohne sagen zu können, welcher welcher ist.
          */}
          <section aria-label={`Zahlung ${gewaehlt.invoice_number}`}>
            <h2>Zahlung {gewaehlt.invoice_number}</h2>

            <dl className="verlauf">
              <dt>Kunde</dt><dd>{gewaehlt.customer_name}</dd>
              <dt>Rechnungsname</dt><dd>{gewaehlt.invoice_name}</dd>
              <dt>Abgleich</dt><dd>{abgleichText(gewaehlt.match_status)}</dd>
              <dt>Raten</dt><dd className="mono">{gewaehlt.raten}</dd>
              <dt>Verbuchter Zahlungsstand</dt><dd><Betrag cents={gewaehlt.processed_cumulative_cents} /></dd>
              <dt>Zuletzt von FinSuites gemeldeter Stand</dt><dd><Betrag cents={gewaehlt.last_reported_cumulative_cents} /></dd>
              {/*
                `summe_cents` zählt nur die Raten (`kind = 'payment'`), der verbuchte Stand auch die
                bestätigten Rückzahlungen. Solange beide gleich sind, ist die Summe der Raten genau der
                verbuchte Stand und sagt nichts Zweites; weicht sie ab, ist die Differenz das, was
                zurückgebucht wurde — und dann gehört sie hierher.
              */}
              {gewaehlt.summe_cents === gewaehlt.processed_cumulative_cents ? null : (
                <>
                  <dt>Summe der Raten (ohne Rückzahlungen)</dt>
                  <dd><Betrag cents={gewaehlt.summe_cents} /></dd>
                </>
              )}
              <dt>Letzte Zahlung</dt><dd className="mono">{datum(gewaehlt.letzte_zahlung)}</dd>
              <dt>Zuletzt geändert</dt><dd className="mono">{datumZeit(gewaehlt.updated_at)}</dd>
            </dl>
            {gewaehlt.last_reported_cumulative_cents < gewaehlt.processed_cumulative_cents ? (
              <p className="warnung">
                FinSuites hat zuletzt weniger gemeldet als verbucht ist. Solange das offen ist, steht dazu eine
                Rückzahlung in der Prüfliste; der verbuchte Stand sinkt erst, wenn Sie sie bestätigen.
              </p>
            ) : null}

            <div className="aktionen">
              <Knopf variante="zweit" onClick={() => { korrektur.reset(); setZiel({ art: 'abschluss' }) }}>Abschluss zuweisen</Knopf>
              <Knopf variante="zweit" onClick={() => { korrektur.reset(); setZiel({ art: 'anlegen' }) }}>Closer zuordnen</Knopf>
              <Knopf variante="zweit" onClick={() => { korrektur.reset(); setZiel({ art: 'produkt' }) }}>Produkt zuweisen</Knopf>
              <Knopf variante="warnung" onClick={() => { korrektur.reset(); setZiel({ art: 'ignorieren' }) }}>
                {gewaehlt.match_status === 'ignoriert' ? 'Ignorierung aufheben' : 'Zahlung ignorieren'}
              </Knopf>
              <Verweis href={`/t/${tenantId}/pruefen`} gehZu={gehZu}>Zur Prüfliste</Verweis>
            </div>
          </section>

          <section aria-label={`Rechnung ${gewaehlt.invoice_number} in FinSuites`}>
            <h3>Die Rechnung in FinSuites</h3>
            <dl className="verlauf">
              <dt>Rechnung netto</dt>
              <dd>{gewaehlt.latest_net_cents === null ? '—' : <Betrag cents={gewaehlt.latest_net_cents} />}</dd>
              <dt>Rechnung brutto</dt>
              <dd>{gewaehlt.latest_gross_cents === null ? '—' : <Betrag cents={gewaehlt.latest_gross_cents} />}</dd>
              <dt>Status in FinSuites</dt><dd>{gewaehlt.latest_status ?? '—'}</dd>
            </dl>
            {gewaehlt.invoice_url === null ? null : istWebLink(gewaehlt.invoice_url) ? (
              <p><a href={gewaehlt.invoice_url} rel="noreferrer noopener" target="_blank">Rechnung in FinSuites öffnen</a></p>
            ) : (
              <p>Rechnungslink aus FinSuites, kein Web-Link: <span className="mono">{gewaehlt.invoice_url}</span></p>
            )}
          </section>

          <section aria-label="Offene Korrekturen">
            <h3>Offene Korrekturen an einzelnen Raten</h3>
            {inbox.isPending ? (
              <p role="status">Wird geladen …</p>
            ) : inbox.isError ? (
              // „Keine." waere hier eine Aussage ueber Daten, die dieser Bildschirm gar nicht hat:
              // solange die Pruefliste nicht geladen ist, weiss er nicht, ob zu dieser Zahlung etwas
              // offen ist. Wer daraufhin nichts korrigiert, korrigiert nichts, weil er nichts sah.
              <p role="alert">
                {fehlermeldung(inbox.error)}{' '}
                <Knopf variante="still" onClick={() => { void inbox.refetch() }}>Erneut versuchen</Knopf>
              </p>
            ) : offeneRaten.length === 0 ? (
              <p>Keine.</p>
            ) : (
              <ul>
                {offeneRaten.map((i) => (
                  <li key={i.subject_id}>
                    <span>{i.label}</span>{' '}
                    {i.amount_cents === null ? null : <Betrag cents={i.amount_cents} />}{' '}
                    {i.kind === 'datum_fehlt' ? (
                      <Knopf onClick={() => { korrektur.reset(); setZiel({ art: 'datum', deltaId: i.subject_id }) }}>Bezahlt am setzen</Knopf>
                    ) : null}
                    {i.kind === 'netto_brutto_unplausibel' ? (
                      <Knopf onClick={() => { korrektur.reset(); setZiel({ art: 'netto', deltaId: i.subject_id }) }}>Netto/Brutto korrigieren</Knopf>
                    ) : null}
                    {i.kind === 'refund_candidate' ? (
                      <>
                        <Knopf onClick={() => { korrektur.reset(); setZiel({ art: 'refund', deltaId: i.subject_id, entscheidung: 'confirm' }) }}>Rückzahlung bestätigen</Knopf>
                        <Knopf variante="still" onClick={() => { korrektur.reset(); setZiel({ art: 'refund', deltaId: i.subject_id, entscheidung: 'ignore' }) }}>Rückzahlung ignorieren</Knopf>
                      </>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            <p className="hinweis">
              Die einzelnen Raten einer Zahlung gibt die Schnittstelle heute nicht heraus; hier stehen die, zu
              denen etwas offen ist — mit dem Betrag, um den es geht.
            </p>
          </section>

          <section aria-label="Änderungsverlauf">
            <h3>Änderungsverlauf</h3>
            {protokoll.isPending ? (
              <p role="status">Wird geladen …</p>
            ) : protokoll.isError ? (
              <p role="alert">
                {fehlermeldung(protokoll.error)}{' '}
                <Knopf variante="still" onClick={() => { void protokoll.refetch() }}>Erneut versuchen</Knopf>
              </p>
            ) : (protokoll.data?.eintraege ?? []).length === 0 ? (
              <p>Bisher nichts geändert.</p>
            ) : (
              <ul>
                {(protokoll.data?.eintraege ?? []).map((e) => (
                  <li key={e.id}>
                    <span className="mono">{datumZeit(e.created_at)}</span> — {protokollText(e.action)}
                    {e.reason === null ? null : <> — „{e.reason}“</>}
                  </li>
                ))}
              </ul>
            )}
            <p className="hinweis">
              Hier steht, was an der Zahlung selbst geändert wurde: Abschluss, Produkt, Ignorierung. Korrekturen
              an einer einzelnen Rate — Zahlungseingang, Netto/Brutto, eine bestätigte oder ignorierte
              Rückzahlung — protokolliert der Server auf der Rate, und die ist über keine Schnittstelle lesbar.
              Der verbuchte Zahlungsstand oben kann also gesunken sein, ohne dass hier etwas dazu steht.
            </p>
          </section>
        </div>
  )

  return (
    <div className="seite">
      <h1>Zahlungen</h1>
      <p role="status" aria-live="polite" className="meldung">{meldungstext}</p>

      {/* Die Filterleiste bleibt in jedem Zustand stehen: wer gerade gefiltert hat, muss sehen, wonach —
          sonst bezieht sich „lädt" auf nichts Sichtbares. Nur die Tabelle weicht dem Lade- und Fehlerzustand.
          Jeder Filterwechsel klappt die gewählte Zahlung zu: sie kann aus der neuen Liste herausfallen, und
          ein Detail zu einer Zeile, die nicht mehr in der Tabelle steht, ist nicht zu belegen. */}
      <Leiste beschriftung="Zahlungen filtern">
        <Feld label="Abgleich" htmlFor="z-status">
          <select id="z-status" value={suche.status ?? ''} onChange={(e) => { setzeSuche({ status: e.target.value === '' ? undefined : e.target.value, zeile: undefined }) }}>
            <option value="">alle</option>
            {Object.keys(ABGLEICH_TEXT).map((s) => <option key={s} value={s}>{abgleichText(s)}</option>)}
          </select>
        </Feld>
        <Feld label="Letzte Zahlung ab" htmlFor="z-von">
          <input id="z-von" type="date" value={suche.von ?? ''} onChange={(e) => { setzeSuche({ von: e.target.value === '' ? undefined : e.target.value, zeile: undefined }) }} />
        </Feld>
        <Feld label="Letzte Zahlung bis" htmlFor="z-bis">
          <input id="z-bis" type="date" value={suche.bis ?? ''} onChange={(e) => { setzeSuche({ bis: e.target.value === '' ? undefined : e.target.value, zeile: undefined }) }} />
        </Feld>
        <Knopf onClick={() => { korrektur.reset(); setZiel({ art: 'erfassen' }) }}>Zahlung von Hand erfassen</Knopf>
      </Leiste>

      {liste.isPending ? <Zustand art="laedt" beschriftung="Die Zahlungen werden geladen." /> : null}
      {liste.isError ? (
        <Zustand art="fehler" fehler={liste.error} onWiederholen={() => { void liste.refetch() }} />
      ) : null}
      {liste.data === undefined ? null : (
        <Tabelle
          beschriftung="Zahlungen"
          kopf={
            <tr>
              <th scope="col">Zahlungs-ID</th>
              <th scope="col">Kunde</th>
              <th scope="col">Rechnungsname</th>
              <th scope="col">Abgleich</th>
              <th scope="col" className="zahl">Raten</th>
              {/* Nicht „Summe": `summe_cents` zählt nur die Raten und lässt bestätigte Rückzahlungen weg
                  (reads.ts, `kind = 'payment'`). Das Detail nennt sie aus demselben Grund so. */}
              <th scope="col" className="zahl">Summe der Raten</th>
              <th scope="col">Letzte Zahlung</th>
              <th scope="col">Detail</th>
            </tr>
          }
        >
          {zahlungen.length === 0 ? (
            <TabellenZustand
              art="leer" spalten={SPALTEN} titel="Noch keine Zahlungen"
              hinweis="Zahlungen kommen aus dem Payment-Webhook. Prüfen Sie unter Einstellungen → Webhook, ob die URL im FinSuites-Workflow eingetragen ist."
            />
          ) : null}
          {zahlungen.map((z) => (
            <Fragment key={z.id}>
            <tr aria-selected={z.id === suche.zeile ? true : undefined}>
              <th scope="row" className="mono">{z.invoice_number}</th>
              <td>{z.customer_name}</td>
              <td>{z.invoice_name}</td>
              <td>
                <Fahne art={ZUGEORDNET.has(z.match_status) ? 'ok' : z.match_status === 'ignoriert' ? 'neutral' : 'warnung'}>
                  {abgleichText(z.match_status)}
                </Fahne>
              </td>
              <td className="zahl mono">{z.raten}</td>
              <td className="zahl"><Betrag cents={z.summe_cents} /></td>
              <td className="mono">{datum(z.letzte_zahlung)}</td>
              <td>
                <Knopf
                  variante="still"
                  onClick={() => { setzeSuche({ zeile: z.id === suche.zeile ? undefined : z.id }) }}
                >
                  {z.id === suche.zeile ? `${z.invoice_number} zuklappen` : `${z.invoice_number} aufklappen`}
                </Knopf>
              </td>
            </tr>
            {detail !== null && z.id === gewaehlt?.id ? (
              <tr className="zeile-detail">
                <td colSpan={SPALTEN}>{detail}</td>
              </tr>
            ) : null}
            </Fragment>
          ))}
        </Tabelle>
      )}

      {ziel === null ? null : ziel.art === 'erfassen' ? (
        <ZahlungVonHandDialog
          {...rahmen}
          erklaerung={<p>Eine Zahlung nachtragen, die FinSuites nicht geliefert hat.</p>}
          vorgabe={{
            invoice_number: '', customer_name: '', invoice_name: '', hl_contact_id: null,
            cumulative_paid_cents: null, source_webhook_event_id: null,
          }}
          onAbsenden={(koerper: ManualPayment) => {
            korrektur.mutate({ meldung: 'Zahlung erfasst.', ruf: () => api.post(`/api/t/${tenantId}/zahlungen/manuell`, koerper) })
          }}
        />
      ) : gewaehlt === null ? null : ziel.art === 'abschluss' ? (
        <AbschlussZuweisenDialog
          {...rahmen} erklaerung={erklaerung} abschluesse={abschlussOptionen} vorgabe={gewaehlt.manual_deal_id}
          onAbsenden={(koerper: ZahlungPatch) => {
            korrektur.mutate({ meldung: 'Abschluss zugewiesen.', ruf: () => api.patch(pfad, koerper) })
          }}
        />
      ) : ziel.art === 'produkt' ? (
        <ProduktZuweisenDialog
          {...rahmen} erklaerung={erklaerung} produkte={produktOptionen} vorgabe={gewaehlt.manual_product_id}
          onAbsenden={(koerper: ZahlungPatch) => {
            korrektur.mutate({ meldung: 'Produkt zugewiesen.', ruf: () => api.patch(pfad, koerper) })
          }}
        />
      ) : ziel.art === 'ignorieren' ? (
        <ZahlungIgnorierenDialog
          {...rahmen} erklaerung={erklaerung} bereitsIgnoriert={gewaehlt.match_status === 'ignoriert'}
          onAbsenden={(koerper: ZahlungPatch) => {
            korrektur.mutate({ meldung: 'Ignorierung geändert.', ruf: () => api.patch(pfad, koerper) })
          }}
        />
      ) : ziel.art === 'anlegen' ? (
        <AbschlussAnlegenDialog
          {...rahmen} erklaerung={erklaerung} produkte={produktOptionen} beteiligte={beteiligte}
          vorgabeProduktId={gewaehlt.manual_product_id ?? gewaehlt.detected_product_id}
          onAbsenden={(koerper: CreateDeal) => {
            korrektur.mutate({ meldung: 'Abschluss angelegt und zugeordnet.', ruf: () => api.post(`${pfad}/create-deal`, koerper) })
          }}
        />
      ) : ziel.art === 'datum' ? (
        <DatumSetzenDialog
          {...rahmen} erklaerung={erklaerung} deltaId={ziel.deltaId}
          onAbsenden={(koerper: ZahlungPatch) => {
            korrektur.mutate({ meldung: 'Zahlungseingang gesetzt.', ruf: () => api.patch(pfad, koerper) })
          }}
        />
      ) : ziel.art === 'netto' ? (
        <NettoBruttoDialog
          {...rahmen}
          erklaerung={
            <>
              {erklaerung}
              <p className="hinweis">
                Vorbelegt sind Netto und Brutto, die FinSuites zuletzt für die <em>Rechnung</em> gemeldet hat —
                nicht die Beträge, die an dieser Rate stehen. Die gibt die Schnittstelle nicht heraus. Bitte gegen
                den Beleg prüfen, bevor Sie speichern.
              </p>
            </>
          }
          deltaId={ziel.deltaId}
          vorgabeNetto={gewaehlt.latest_net_cents} vorgabeBrutto={gewaehlt.latest_gross_cents}
          onAbsenden={(koerper: ZahlungPatch) => {
            korrektur.mutate({ meldung: 'Netto und Brutto korrigiert.', ruf: () => api.patch(pfad, koerper) })
          }}
        />
      ) : (
        <RueckzahlungDialog
          {...rahmen} erklaerung={erklaerung} entscheidung={ziel.entscheidung}
          onAbsenden={(koerper: RefundDecision) => {
            korrektur.mutate({
              meldung: ziel.entscheidung === 'confirm' ? 'Rückzahlung bestätigt.' : 'Rückzahlung ignoriert.',
              ruf: () => api.post(`${pfad}/refund/${ziel.deltaId}/${ziel.entscheidung}`, koerper),
            })
          }}
        />
      )}
    </div>
  )
}

export const Route = createFileRoute('/t/$tenantId/zahlungen')({
  validateSearch: (roh: Record<string, unknown>): ZahlungenSuche => {
    const text = (schluessel: string): string | undefined => {
      const wert = roh[schluessel]
      return typeof wert === 'string' && wert !== '' ? wert : undefined
    }
    return { status: text('status'), von: text('von'), bis: text('bis'), zeile: text('zeile') }
  },
  component: ZahlungenRoute,
})

function ZahlungenRoute(): ReactNode {
  const { tenantId } = Route.useParams()
  const suche = Route.useSearch()
  const navigate = useNavigate()
  return (
    <ZahlungenSeite
      tenantId={tenantId}
      suche={suche}
      setzeSuche={(naechste) => { void navigate({ to: '.', search: (alt: ZahlungenSuche) => ({ ...alt, ...naechste }) }) }}
      gehZu={(pfad) => { void navigate({ to: pfad }) }}
    />
  )
}
